import { Clock, Mail, MapPin, MessageCircle, Phone } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { ArrowIcon } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { Reveal } from '@/components/ui/Reveal';
import { SectionIntro } from '@/components/ui/SectionIntro';
import { InquiryForm, type InquiryContext } from '@/features/inquiry/InquiryForm';
import { uniquePhones, whatsappLink } from '@/lib/utils';
import { WhyUs } from './home/WhyUs';
import { useSite } from './SiteProvider';

/** Verified strengths (shared by About and Private Label pages). */
export function CapabilitiesSection() {
  return <WhyUs showTerms={false} />;
}

/** MOQ / sampling / production / shipping / payment terms band. */
export function ProductionInfoStrip() {
  const { productionInfo } = useSite().content;
  if (productionInfo.items.length === 0) return null;
  return (
    <section aria-labelledby="terms-title" className="bg-primary text-white">
      <div className="container-page py-12">
        <h2 id="terms-title" className="eyebrow font-sans text-steel">
          {productionInfo.heading || 'Commercial terms'}
        </h2>
        <dl className="mt-6 grid gap-x-8 gap-y-6 sm:grid-cols-2 lg:grid-cols-5">
          {productionInfo.items.map((i) => (
            <div key={i.label} className="lg:border-l lg:border-white/15 lg:pl-6 lg:first:border-l-0 lg:first:pl-0">
              <dt className="text-[12.5px] font-medium tracking-[0.08em] text-steel uppercase">{i.label}</dt>
              <dd className="mt-2 font-display text-[16px] leading-snug font-bold">{i.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

/** Full private-label journey with descriptions and per-step calls to action. */
export function ProcessSection({ showCtas = false }: { showCtas?: boolean }) {
  const { process } = useSite().content;
  return (
    <section aria-labelledby="process-title" className="section-y bg-primary-dark text-white">
      <div className="container-page">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <p className="eyebrow flex items-center gap-3 text-steel">
              <span aria-hidden className="h-px w-8 bg-accent" />
              {process.eyebrow}
            </p>
            <h2 id="process-title" className="display-lg mt-4">
              {process.heading} {process.headingMuted && <span className="editorial-italic block text-steel">{process.headingMuted}</span>}
            </h2>
          </div>
          {process.intro && <p className="text-[16px] leading-[1.7] text-steel lg:col-span-5">{process.intro}</p>}
        </div>

        <ol className="mt-14 grid gap-px overflow-hidden rounded-[8px] border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-4">
          {process.steps.map((step, i) => (
            <Reveal as="li" key={step.title} delay={i * 80} className="flex flex-col bg-primary-dark p-7">
              <div className="flex items-center justify-between">
                <span className="font-display text-[34px] leading-none font-light text-steel tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                <Icon name={step.icon} className="h-5 w-5 text-accent-soft" />
              </div>
              <h3 className="mt-6 font-display text-[19px] font-bold tracking-tight">{step.title}</h3>
              <p className="mt-2.5 text-[14.5px] leading-[1.65] text-steel">{showCtas ? step.description : step.summary}</p>
              {step.points.length > 0 && (
                <ul className="mt-5 space-y-1.5 border-t border-white/10 pt-4">
                  {step.points.map((p) => (
                    <li key={p} className="flex items-center gap-2 text-[13.5px] text-white/85">
                      <span aria-hidden className="h-1 w-1 rounded-full bg-accent" />
                      {p}
                    </li>
                  ))}
                </ul>
              )}
              {showCtas && step.ctaLabel && (
                <Link to="#inquiry" className="mt-auto inline-flex items-center gap-2 pt-6 text-[14px] font-semibold text-accent-soft hover:text-white">
                  {step.ctaLabel} <ArrowIcon />
                </Link>
              )}
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function ContactDetails() {
  const { contact } = useSite().settings;
  const phones = uniquePhones(contact.phones);
  const rows: { icon: typeof Mail; label: string; value: ReactNode }[] = [
    ...contact.locations.map((l) => ({ icon: MapPin, label: l.label, value: <span>{l.address}</span> })),
    ...(phones.length
      ? [
          {
            icon: Phone,
            label: 'Telephone / WhatsApp',
            value: (
              <span className="flex flex-col">
                {phones.map((p) => (
                  <a key={p.number} href={`tel:${p.number.replace(/[^\d+]/g, '')}`} className="hover:text-accent-dark hover:underline">
                    {p.number}
                  </a>
                ))}
              </span>
            ),
          },
        ]
      : []),
    ...(contact.emails.length
      ? [
          {
            icon: Mail,
            label: 'Email',
            value: (
              <span className="flex flex-col">
                {contact.emails.map((e) => (
                  <a key={e.email} href={`mailto:${e.email}`} className="break-all hover:text-accent-dark hover:underline">
                    {e.email}
                  </a>
                ))}
              </span>
            ),
          },
        ]
      : []),
    ...(contact.workingHours
      ? [{ icon: Clock, label: 'Business hours', value: <span>{contact.workingHours}</span> }]
      : []),
  ];

  return (
    <div>
      <ul className="divide-y divide-line border-y border-line" aria-label="Contact details">
        {rows.map(({ icon: IconC, label, value }) => (
          <li key={label} className="flex gap-4 py-5">
            <IconC className="mt-0.5 h-[18px] w-[18px] shrink-0 text-accent-dark" aria-hidden />
            <div className="min-w-0">
              <p className="text-[12px] font-semibold tracking-[0.12em] text-muted uppercase">{label}</p>
              <div className="mt-1 text-[15px] font-medium text-primary">{value}</div>
            </div>
          </li>
        ))}
      </ul>
      {contact.responseTime && <p className="mt-5 text-[14px] text-muted">{contact.responseTime}</p>}
      {contact.whatsappNumber && (
        <a
          href={whatsappLink(contact.whatsappNumber)}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 inline-flex items-center gap-2 text-[14px] font-semibold text-primary underline-offset-4 hover:underline"
        >
          <MessageCircle className="h-4 w-4 text-whatsapp" aria-hidden /> Chat on WhatsApp
        </a>
      )}
    </div>
  );
}

export function MapEmbed() {
  const location = useSite().settings.contact.locations.find((l) => l.mapEmbedUrl);
  if (!location) return null;
  return (
    <div className="self-start overflow-hidden rounded-[8px] border border-line bg-surface">
      <iframe
        title={`Map: ${location.address}`}
        src={location.mapEmbedUrl}
        className="aspect-[16/10] w-full border-0 grayscale-[35%]"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        sandbox="allow-scripts allow-same-origin allow-popups"
      />
      {location.mapLinkUrl && (
        <a
          href={location.mapLinkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-between border-t border-line bg-white px-5 py-3 text-[14px] font-semibold text-primary hover:bg-surface"
        >
          Open in Google Maps <ArrowIcon />
        </a>
      )}
    </div>
  );
}

/** Quote form section (`#inquiry`) used on every public page that converts. */
export function InquirySection({
  context,
  heading,
  intro,
  eyebrow = 'Request a quote',
  formTitle,
  showMap = false,
  showDetails = true,
}: {
  context?: InquiryContext;
  heading?: string;
  intro?: string;
  eyebrow?: string;
  formTitle?: string;
  showMap?: boolean;
  /** Hide the contact details column (e.g. on the Contact page, which already shows them). */
  showDetails?: boolean;
}) {
  const { contactPage } = useSite().content;
  return (
    <section id="inquiry" aria-labelledby="inquiry-heading" className="section-y bg-surface">
      <div className="container-page grid gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <SectionIntro id="inquiry-heading" eyebrow={eyebrow} title={heading ?? contactPage.heading} intro={intro ?? contactPage.intro} size="md" />
          {showDetails && (
            <div className="mt-10">
              <ContactDetails />
            </div>
          )}
          {showMap && (
            <div className="mt-8">
              <MapEmbed />
            </div>
          )}
        </div>
        <div className="lg:col-span-7">
          <InquiryForm context={context} title={formTitle} />
        </div>
      </div>
    </section>
  );
}
